import { DealTypeToId } from 'constants/deals';
import { camelCase } from 'lodash';
import { IExcelMappingCellSchema } from 'queries/UploadedProForma/useCreateProFormaMapping';
import { IUploadedProForma } from 'types/db/UploadedProForma';
import { IInvalidRows, IMapping, IProFormaMappingRow } from './types';
import {
    ACQUISITIONS_DEAL_TABLE_NAME_TO_ROW_KEY_NAME, DEVELOPMENT_DEAL_TABLE_NAME_TO_ROW_KEY_NAME,
    EXCEL_LAST_COLUMN_XFD, EXCEL_LAST_ROW, ProFormaMappingTableNames,
} from './constants';

export const isValidExcelCell = (cell: string) => {
    if (!cell) return false;

    const cellName = cell.trim().toUpperCase();

    // excel can only have 1-3 uppercase letters and 1-7 digits
    const regex = /^[A-Z]{1,3}[1-9][0-9]{0,6}$/;
    if (!regex.test(cellName)) return false;

    // extract column letters and row number
    const match = cellName.match(/^([A-Z]{1,3})(\d{1,7})$/);
    if (!match) return false;

    const [, colLetters, rowStr] = match;
    const row = parseInt(rowStr, 10);

    // check against the max row
    if (row > EXCEL_LAST_ROW) return false;

    // check what number column letters correspond to
    const colNum = [...colLetters].reduce((acc: any, char: string) => {
        const charCode = char.charCodeAt(0);
        if (charCode < 65 || charCode > 90) return false;
        return (acc * 26) + (charCode - 64);
    }, 0);

    // excel's max column is XFD => 16384
    return Number(colNum) <= EXCEL_LAST_COLUMN_XFD;
};

export const matchMappingWithDefaultStructure = (dealTypeId: number, savedMapping: IProFormaMappingRow[]) => {
    const isDevelopment = dealTypeId === DealTypeToId.Development;

    const tableNameToRowKeyName = isDevelopment
        ? DEVELOPMENT_DEAL_TABLE_NAME_TO_ROW_KEY_NAME
        : ACQUISITIONS_DEAL_TABLE_NAME_TO_ROW_KEY_NAME as Record<string, ProFormaMappingTableNames>;

    if (savedMapping.length) {
        const groupedMapping = {} as IMapping;
        savedMapping.map((row, index) => {
            const tableKey = row.isSource
                ? ProFormaMappingTableNames.DEVELOPMENT_SOURCES
                : tableNameToRowKeyName[row.name];

            if (!groupedMapping[tableKey]) {
                groupedMapping[tableKey] = [];
            }

            // Add ID to source rows
            const rowWithId = row.isSource ? { ...row, id: index + 1 } : row;
            groupedMapping[tableKey]!.push(rowWithId);
            return null;
        });

        return groupedMapping;
    }
    return null;
};

export const getMappingData = (mapping: Partial<Record<string, IProFormaMappingRow[]>>, dealTypeId: number) => {
    const mappedData = mapping
        ? Object.entries(mapping)
            .filter(([key]) => key !== ProFormaMappingTableNames.DEVELOPMENT_SOURCES)
            .flatMap(([_, value]) => value)
        : [];

    const mappedDataMatchingKeys = mappedData.reduce((acc, item: any) => {
        const key = camelCase(item.name);
        acc[key] = { name: item.name, sheet: item.sheet, cell: item.cell };
        return acc;
    }, {} as Record<string, IExcelMappingCellSchema>);

    const sourcesMappedData = mapping?.[ProFormaMappingTableNames.DEVELOPMENT_SOURCES]?.map(row => ({
        name: row.name,
        sheet: row.sheet,
        cell: row.cell,
    }));

    const baseTemplateMappingData = {
        dealTypeId,
        unleveredIrr: mappedDataMatchingKeys.unleveredIrr,
        leveredIrr: mappedDataMatchingKeys.leveredIrr,
        limitedPartnerIrr: mappedDataMatchingKeys.limitedPartnerIrr,
        generalPartnerIrr: mappedDataMatchingKeys.generalPartnerIrr,
        untrendedYieldOnCost: mappedDataMatchingKeys.untrendedYieldOnCost,
        trendedYieldOnCost: mappedDataMatchingKeys.trendedYieldOnCost,
        effectiveGrossRevenue: mappedDataMatchingKeys.effectiveGrossRevenue,
        totalOperatingExpenses: mappedDataMatchingKeys.totalOperatingExpenses,
        netOperatingIncome: mappedDataMatchingKeys.netOperatingIncome,
        capitalExpenditures: mappedDataMatchingKeys.capitalExpenditures,
        cashFlowFromOps: mappedDataMatchingKeys.cashFlowFromOperations,
        valuation: mappedDataMatchingKeys.valuation,
    };

    const developmentData = {
        ...baseTemplateMappingData,
        acquisitionCosts: mappedDataMatchingKeys.acquisitionCosts,
        hardCosts: mappedDataMatchingKeys.hardCosts,
        softCosts: mappedDataMatchingKeys.softCosts,
        financingCosts: mappedDataMatchingKeys.financingCosts,
        totalUses: mappedDataMatchingKeys.totalUses,
        sources: sourcesMappedData,
    };

    const acquisitionData = {
        ...baseTemplateMappingData,
        acquisitionCosts: mappedDataMatchingKeys.acquisitionCosts,
        sources: undefined,
    };

    return dealTypeId === DealTypeToId.Development ? developmentData : acquisitionData;
};

export const getPageTitle = (uploadedProForma: IUploadedProForma | undefined, proFormaId: string | undefined, isEditMode: boolean) => {
    const uploadedProFormaFileName = uploadedProForma ? uploadedProForma?.s3FilePath.split('/').pop() || '' : '';

    let pageTitle = 'Upload pro forma';

    if (proFormaId && isEditMode) {
        pageTitle = 'Edit pro forma mapping';
    } else {
        pageTitle = uploadedProFormaFileName;
    }

    return pageTitle;
};

export const getPageSubitle = (proFormaId: string | undefined, isEditMode: boolean) => {
    let pageSubtitle = 'Use an existing .xls or .xlsx file to populate a new pro forma.';

    if (proFormaId && isEditMode) {
        pageSubtitle = 'Edit the mapping for your uploaded pro forma.';
    } else {
        pageSubtitle = '';
    }

    return pageSubtitle;
};

export const countMappingErrors = (mapping: Partial<Record<string, IProFormaMappingRow[]>>, sheetNames: string[]) => {
    let count = 0;
    const errorRows = {} as IInvalidRows;

    // Check for duplicate names within each mapping group
    Object.entries(mapping).forEach(([key, rows]) => {
        const nameCounts = new Map<string, number>();

        rows?.forEach((row: any) => {
            const name = String(row.name).trim();
            if (name) {
                nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
            }
        });

        // Add duplicate name errors
        rows?.forEach((row: any) => {
            const name = String(row.name).trim();
            if ((nameCounts.get(name) ?? 0) > 1) {
                errorRows[row.name] = {
                    ...(errorRows[row.name] || { nameError: false, sheetError: false, cellError: false }),
                    nameError: true,
                };
                count += 1;
            }
        });
    });

    // Check other validations
    Object.values(mapping).flat().forEach((row: any) => {
        const sheet = String(row.sheet).trim();
        const cell = String(row.cell).trim();
        const name = String(row.name).trim();

        if (sheet === '' && cell === '') return;

        const isValidSheet = sheetNames.includes(sheet);
        const isValidCell = isValidExcelCell(cell);
        const isValidName = row.isSource ? name !== '' : true;

        if (!(isValidSheet && isValidCell && isValidName)) {
            count += 1;
            if (!errorRows[row.name]) {
                errorRows[row.name] = { nameError: false, sheetError: false, cellError: false };
            }
            errorRows[row.name] = {
                ...errorRows[row.name],
                nameError: !isValidName,
                sheetError: !isValidSheet,
                cellError: !isValidCell,
            };
        }
    });

    return { count, errorRows };
};
